<template>
	<div class="from-filter-container">
		<FilterDropdown
			v-model="selectedUserIds"
			:options="options"
			:loading="isLoading"
			:filterFn="filterUser"
			:searchPlaceholder="t('search_from_filter_placeholder')"
			optionsClass="search-from-filter-options"
			@load-more="searchDebounced"
			@open="onOpen"
			@clear="onClear"
			@change="onChange"
			@search="onSearch"
		>
			<template #placeholder="{ items }">
				<span
					class="from-placeholder"
					v-if="!items.length || isLoadingInit"
					v-text="t('search_users_filter_title')"
				/>
				<span class="from-placeholder-loading" v-if="isLoadingInit">
					<Spinner />
				</span>
				<span
					class="from-placeholder"
					v-if="items.length > 0 && items[0].label"
					v-text="
						t('search_users_filter_title_with_value', { User: items[0].label })
					"
				/>
			</template>

			<template v-slot="{ option }">
				<div class="from-option-user">
					<ProfilePic :user="option.user" size="26px" />
					<div class="from-option-dm-name" v-text="option.label" />
				</div>
			</template>
		</FilterDropdown>
	</div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import FilterDropdown from './FilterDropdown'
import { ProfilePic, Spinner } from '@/components/common'
import debounce from 'debounce'
import { fetchUserInfo, searchUsers } from '@/api/user'
import { getUser, setUsers, sortUsersByFullname } from '@/store'
import { removeDuplicatesByField } from '@/helpers/utils'
import bus from '@/helpers/bus'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()
let apiResult = null

const model = defineModel({ required: true })
const emit = defineEmits(['open', 'search'])

const allUsers = ref([])
const selectedUserIds = ref(model.value || [])
const showOnlySelected = ref(selectedUserIds.value.length > 0)
const isOpen = ref(false)
const searchTerm = ref(null)
const isLoading = ref(false)
const isLoadingInit = ref(false)

const searchDebounced = debounce(fetchUsers, 250)

if (selectedUserIds.value.length) {
	fetchSpecificUsers(selectedUserIds.value)
}

watch(selectedUserIds, (userIds) => {
	model.value = userIds || null
	switchToOnlySelected()
})

watch(model, (newModelValue) => {
	selectedUserIds.value = newModelValue || []
	switchToOnlySelected()
})

const options = computed(() => {
	if (isLoadingInit.value) return []
	if (searchTerm.value) return allUsers.value.map((u) => mapUser(u))
	if (selectedUserIds.value.length)
		return selectedUserIds.value.map(getUser).map(mapUser)
	return allUsers.value.map((u) => mapUser(u))
})

const hasSelected = computed(() => selectedUserIds.value.length > 0)

function onOpen(open) {
	emit('open', open)
	isOpen.value = open
	if (!open) {
		switchToOnlySelected()
	}
}

function switchToOnlySelected() {
	showOnlySelected.value = hasSelected.value
	captureOnlySelected()
	bus.$emit('clear-dropdown-search')
}

function onChange(userOptions) {
	setUsers(userOptions.map((o) => o.user))
}

function onClear() {
	allUsers.value = []
	showOnlySelected.value = false
}

function captureUsers(users) {
	allUsers.value = removeDuplicatesByField(
		[...allUsers.value, ...users],
		'id'
	).sort(sortUsersByFullname)
}

function captureOnlySelected() {
	if (hasSelected.value) {
		allUsers.value = allUsers.value.filter((u) =>
			selectedUserIds.value.includes(u.id)
		)
	} else {
		allUsers.value = []
	}
}

function filterUser(item, regex) {
	return item?.user?.fullname ? regex.test(item.user.fullname) : false
}

function onSearch(term) {
	emit('search', term)
	showOnlySelected.value = false
	searchTerm.value = term || null
	apiResult = null
	if (!term) return
	isLoading.value = true
	searchDebounced()
}

function mapUser(user, order) {
	if (!user) return null
	return {
		id: user.id,
		label: user.fullname,
		user,
		order: order >= 0 ? order : undefined,
	}
}

async function fetchUsers() {
	try {
		let result = null
		if (apiResult?.next) {
			result = await apiResult.next()
		} else {
			result = await searchUsers(searchTerm.value)
		}
		apiResult = result
		captureUsers(apiResult.data)
	} catch (e) {
		console.error(e)
	}
	isLoading.value = false
}

async function fetchSpecificUsers(userIds) {
	try {
		let users = userIds.map(getUser).filter((u) => u)
		if (users.length === userIds.length) {
			captureUsers(users)
			return users
		}
		isLoadingInit.value = true
		const missingUsers = userIds.filter((id) => !getUser(id))
		const promises = missingUsers.map((id) => fetchUserInfo(id))
		const results = await Promise.all(promises)
		users = results.filter((r) => r.ok).map((r) => r.user)
		setUsers(users)
		bus.$emit('from-filter-users', users)
	} catch (e) {
		console.error(e)
	}
	isLoadingInit.value = false
}
</script>

<style lang="scss" scoped>
@import 'src/sass/variables';

.from-option-user {
	@include flex(8px);
	width: 100%;
	max-width: 100%;
	.from-option-dm-name {
		flex-grow: 1;
		@include ellipsis;
	}
}

.from-option-header {
	color: var(--neutral-600);
	background: var(--neutral-100);
	padding: 4px 16px;
	margin-bottom: 4px;
	font-size: 12px;
}
</style>
<style lang="scss">
@import 'src/sass/variables';

.search-from-filter-options {
	@include flex-column;
	&.loading {
		min-height: 90px;
	}
	.scroller {
		flex-grow: 1;
	}
	.dropdown-option {
		width: calc(100% - 8px) !important;
		margin: 4px !important;
		&:first-child {
			&.no-hover {
				margin: 0 0 4px 0 !important;
				.from-option-category {
					margin: 0 0 4px 0 !important;
				}
			}
		}
		&.no-hover {
			height: 24px !important;
			width: 100% !important;
			margin: 4px 0 !important;
		}
	}
}

@include overrideScheme('dark') {
	.from-filter-container {
		.dropdown-container {
			.dropdown-selected {
				.search-filter-placeholder {
					color: white;
				}
			}
		}
		.search-filter-container.active {
			.dropdown-selected {
				.pronto-icon.chevron {
					color: white;
				}
			}
		}
	}
	.search-from-filter-options {
		background: var(--neutral-200) !important;
		.dropdown-search {
			background: var(--neutral-200) !important;
			.pronto-input {
				background-color: unset !important;
			}
		}
	}
}
</style>
